import arrayMove from 'array-move';
import { useQuery, useMutation } from '@apollo/client';
import qs from 'qs';
import React, { useReducer, useEffect, useState } from 'react';
import { v4 } from 'uuid';

import { ErrorHandler } from '@playbookux/playbook-web-core';

import {
  filter, find, isEmpty, map,
} from 'lodash';
import {
  bool, func, shape, string,
} from 'prop-types';
import {
  ERROR_MESSAGE_ITEM,
  ERROR_MESSAGE_CATEGORY,
} from './constants';

import CardSortCategories from './card-sort-categories';
import CardSortItems from './card-sort-items';
import SubmitModal from '../../components/modal-submit';
import WarningModal from './warning-modal';
import SubmitNewStudy from './submit-new-study';
import ResponsiveDndProvider from './responsive-dnd-provider';

import reducer, { initialState } from './reducer';
import useDispatchToActions from '../../../core/utils/dispatch';
import {
  addCardSortCategoryItem,
  addCategoryWithItem,
  addEmptyCategory,
  moveCardSortCategory,
  moveCardSortItem,
  moveTestSessionCardSortItem,
  removeCardSortCategory,
  removeCardSortCategoryItem,
  setCardSortCategories,
  setCardSortItems,
  setCardSortSettings,
  setTestSessionId,
  updateCardSortCategory,
} from './actions';

import {
  CARD_SORT_QUERIES,
  CREATE_TEST_SESSION_CARD_SORT,
} from './graphql';

import {
  Button,
  Container,
  Content,
  Header,
  Loader,
} from './styles';
import { handleRedirectSession } from '../../utils/handleRedirectSession';
import { Message } from '../recruit-participants/styles';
import TEST_SESSION_STATUS from '../../../core/constants/test-session-status';
import InstructionsModal from './instructions-modal';

const CardSort = (props) => {
  const {
    location,
    history,
    isPreview,
  } = props;

  const { search } = location;
  const {
    projectId,
    'session-id': sessionId = '',
  } = qs.parse(search, { ignoreQueryPrefix: true });

  const [categoyError, setCategoyError] = useState(null);
  const [itemError, setItemError] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isSortedCards, setIsSortedCards] = useState(false);
  const [showModalOnce, setShowModalOnce] = useState(false);
  const [categories, setCategories] = useState([]);

  const [state, dispatch] = useReducer(reducer, initialState);

  const dispatchToActions = useDispatchToActions({
    addCardSortCategoryItem,
    addCategoryWithItem,
    addEmptyCategory,
    moveCardSortCategory,
    moveCardSortItem,
    moveTestSessionCardSortItem,
    removeCardSortCategory,
    removeCardSortCategoryItem,
    setCardSortCategories,
    setCardSortItems,
    setCardSortSettings,
    setTestSessionId,
    updateCardSortCategory,
  }, dispatch);

  const {
    addCardSortCategoryItem: onAddCardSortCategoryItem,
    addCategoryWithItem: onAddCategoryWithItem,
    addEmptyCategory: onAddEmptyCategory,
    moveCardSortCategory: onMoveCardSortCategory,
    moveCardSortItem: onMoveCardSortItem,
    moveTestSessionCardSortItem: onMoveTestSessionCardSortItem,
    removeCardSortCategory: onRemoveCardSortCategory,
    removeCardSortCategoryItem: onRemoveCardSortCategoryItem,
    setCardSortCategories: onSetCardSortCategories,
    setCardSortItems: onSetCardSortItems,
    setCardSortSettings: onSetCardSortSettings,
    setTestSessionId: onSetTestSessionId,
    updateCardSortCategory: onUpdateCardSortCategory,
  } = dispatchToActions;

  const {
    data = {},
    error,
    loading,
  } = useQuery(CARD_SORT_QUERIES, {
    variables: { projectId, testSessionId: sessionId },
    skip: !projectId,
  });
  const { project = {}, testSessions = [], recruitParticipantToken = [] } = data || {};
  const {
    thankYouRedirectLink = '',
    thankYouTitle = '',
    thankYouContent = '',
    hasMrId = false,
  } = project || {};
  const [createTestSessionCardSort, {
    loading: isLoading,
    error: mutationError,
  }] = useMutation(CREATE_TEST_SESSION_CARD_SORT);

  const {
    cardSortCategories = [],
    hideName,
    cardSortItems,
    sortType,
    testSessionId = '',
  } = state;

  const errors = [];
  if (itemError) errors.push({ key: itemError, message: itemError });
  if (categoyError) errors.push({ key: categoyError, message: categoyError });

  useEffect(() => {
    setShowInstructions(true);
  }, []);

  useEffect(() => {
    const hasInvalidCategory = cardSortCategories.find((category) => !category.name);
    if (!hasInvalidCategory && isEmpty(cardSortItems) && !showModalOnce) {
      setShowSubmitModal(true);
      setIsSortedCards(true);
    } else {
      setShowSubmitModal(false);
    }
  }, [cardSortCategories, cardSortItems, showModalOnce]);

  useEffect(() => {
    if (sessionId && !testSessionId) {
      onSetTestSessionId(sessionId);
    }
  }, [onSetTestSessionId, sessionId, testSessionId]);

  // save card sort items, categories and settings in redux
  useEffect(() => {
    if (isEmpty(data)) return;

    if (project && !isEmpty(project)) {
      onSetCardSortSettings(project);
    }

    if (data.cardSortItems) {
      onSetCardSortItems(data.cardSortItems);
    }

    if (data.cardSortCategories) {
      const newCardSortCategories = map(data.cardSortCategories, (cardSortCategory) => ({
        ...cardSortCategory,
        cardSortItems: [],
        projectId,
      }));

      onSetCardSortCategories(newCardSortCategories);
    }
  }, [data, onSetCardSortCategories, onSetCardSortItems, onSetCardSortSettings, onSetTestSessionId, projectId, sessionId, testSessionId]);

  useEffect(() => {
    setCategories(cardSortCategories);
  }, [cardSortCategories]);

  const handleCreate = () => {
    const testSessionCardSortCategories = map(cardSortCategories, (cardSortCategory) => {
      const testSessionCardSortCategoryId = v4();

      const { categoryOrder } = cardSortCategory;

      const testSessionCardSortItems = map(cardSortCategory.cardSortItems, ({
        id: cardSortItemId,
        itemOrder,
      }) => ({
        testSessionCardSortCategoryId,
        cardSortItemId,
        itemOrder,
      }));

      return {
        id: testSessionCardSortCategoryId,
        testSessionId,
        cardSortCategoryId: cardSortCategory.id,
        cardSortItemAttributes: testSessionCardSortItems,
        categoryOrder,
      };
    });

    const cardSortCategoryAttributes = map(cardSortCategories, (cardSortCategory) => ({
      id: cardSortCategory.id,
      name: cardSortCategory.name,
      isDefault: cardSortCategory.isDefault,
      categoryOrder: cardSortCategory.categoryOrder,
      projectId: cardSortCategory.projectId,
    }));

    createTestSessionCardSort({
      variables: {
        testSessionId,
        attributes: {
          testSessionCategoryAttributes: testSessionCardSortCategories,
          cardSortCategoryAttributes,
        },
      },
    }).then((response) => {
      if (!response.error && !isLoading) {
        if (!thankYouRedirectLink) {
          history.push({
            pathname: '/thank-you',
            state: {
              title: thankYouTitle,
              content: thankYouContent,
            },
          });
        } else {
          const testSessionMrId = testSessions[0]?.tester?.mrId;
          handleRedirectSession(thankYouRedirectLink, hasMrId, testSessionMrId);
        }
      }
    });
  };

  const handleSubmit = () => {
    const categoryErrors = map(cardSortCategories, ({ name }, index) => ({
      error: !name || isEmpty(name),
      index,
    }));

    const hasItemError = !isEmpty(cardSortItems) ? ERROR_MESSAGE_ITEM : null;
    const hasCategoryError = !isEmpty(filter(categoryErrors, { error: true })) ? ERROR_MESSAGE_CATEGORY : null;

    setItemError(hasItemError);
    setCategoyError(hasCategoryError);

    if (hasItemError || hasCategoryError) {
      setShowSubmitModal(false);
      setShowWarningModal(true);
      return;
    }

    if (isPreview) {
      history.push({
        pathname: '/thank-you',
        state: {
          title: project?.thankYouTitle,
          content: project?.thankYouContent,
        },
      });
      return;
    }

    if (!hasItemError && !hasCategoryError && !isPreview) {
      handleCreate();
    }
  };

  const handleMoveCategoriesCopy = (id, index) => {
    const { categoryOrder } = find(categories, { id });

    const categoriesList = arrayMove(categories, categoryOrder, index).map((category, idx) => ({
      ...category,
      categoryOrder: idx,
    }));

    setCategories(categoriesList);
  };

  const handleMoveItemBack = (cardSortItem, index) => {
    const updatedItem = {
      ...cardSortItem,
      itemOrder: cardSortItems.length,
      cardSortCategoryId: null,
    };

    const items = arrayMove([...cardSortItems, updatedItem], cardSortItems.length - 1, index)
      .map((item, idx) => ({
        ...item,
        itemOrder: idx,
      }));

    onSetCardSortItems(items);
  };

  const handleCreateCategory = (cardSortItem = null) => {
    if (!cardSortItem) {
      onAddEmptyCategory(projectId);
      return;
    }

    onAddCategoryWithItem(cardSortItem, projectId);
  };

  const handleCloseSubmitModal = () => {
    setShowSubmitModal(false);
    setShowModalOnce(true);
  };

  const handleFinishStudy = () => {
    setShowSubmitModal(true);
    setIsSortedCards(false);
  };

  if (loading) {
    return (
      <Container>
        <Loader data-loader="card-sort-loader" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorHandler
          error={error}
          defaultMessage="Error while retrieving Test Session."
        />
      </Container>
    );
  }

  if (data && !isPreview) {
    if (testSessions[0].status === TEST_SESSION_STATUS.processed && recruitParticipantToken[0]) {
      return (
        <SubmitNewStudy
          history={history}
          token={recruitParticipantToken[0].token}
        />
      );
    }
  }

  if (testSessions[0] && new Date(testSessions[0].expiresAt) < new Date()) {
    return (
      <Container center>
        <Message>Test session expired</Message>
      </Container>
    );
  }

  return (
    <Container>
      <SubmitModal
        onClose={handleCloseSubmitModal}
        onSubmit={handleSubmit}
        show={showSubmitModal}
        loading={isLoading}
        error={mutationError}
        isSortedCards={isSortedCards}
      />

      {showWarningModal && (
        <WarningModal
          onClose={() => setShowWarningModal(false)}
          errors={errors}
          show={showWarningModal}
        />
      )}

      {showInstructions && (
        <InstructionsModal
          show={showInstructions}
          title={project?.instructionsTitle}
          content={project?.instructionsContent}
          label="OK, I'm ready!"
          onClose={() => setShowInstructions(false)}
        />
      )}

      <Header>
        <Button.Inverted onClick={() => setShowInstructions(true)}>
          Instructions
        </Button.Inverted>
        <Button onClick={handleFinishStudy}>
          Finish
        </Button>
      </Header>

      <Content>
        <ResponsiveDndProvider>
          <CardSortItems
            hideTooltip={project?.tooltipDescription}
            hideItemName={hideName}
            items={cardSortItems}
            onMove={onMoveCardSortItem}
            onMoveItemBack={handleMoveItemBack}
          />
          <CardSortCategories
            categories={categories}
            onMoveCategoriesCopy={handleMoveCategoriesCopy}
            hideItemName={hideName}
            onAddCategory={handleCreateCategory}
            onAddItem={onAddCardSortCategoryItem}
            onMoveCategory={onMoveCardSortCategory}
            onMoveItem={onMoveTestSessionCardSortItem}
            onRemoveCategory={onRemoveCardSortCategory}
            onRemoveCategoryItem={onRemoveCardSortCategoryItem}
            onUpdateCategory={onUpdateCardSortCategory}
            sortType={sortType}
          />
        </ResponsiveDndProvider>
      </Content>
    </Container>
  );
};

CardSort.propTypes = {
  location: shape({
    search: string.isRequired,
  }).isRequired,
  history: shape({
    push: func,
  }).isRequired,
  isPreview: bool,
};

CardSort.defaultProps = {
  isPreview: false,
};

export default CardSort;
